import { clamp } from '../utils/index.js';
/**
 * CIELAB companding constants — the EXACT CIE 15:2004 rationals.
 *
 * Kept numerically identical to `ColorConverter`'s pair on purpose: these two
 * modules deliberately keep separate LAB implementations (see
 * `conversions.equivalence.test.ts` for why), but they must not disagree about
 * the STANDARD. The previous decimals here (`0.008856` with a `7.787` linear
 * slope) were the pre-2004 approximations; `7.787` is just kappa/116 rounded.
 */
const CIE_EPSILON = 216 / 24389; // (6/29)^3 exactly
const CIE_KAPPA = 24389 / 27; // (29/3)^3 exactly
// ============================================================================
// CIELAB (D65 illuminant)
// ============================================================================
/**
 * RGB to CIELAB conversion.
 * Exported for use in SVG generators that display LAB values.
 */
export function rgbToLab(rgb) {
    let r = rgb.r / 255;
    let g = rgb.g / 255;
    let b = rgb.b / 255;
    // sRGB to linear
    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
    // Convert to XYZ (D65 illuminant)
    let x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) / 0.95047;
    let y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
    let z = (r * 0.0193339 + g * 0.119192 + b * 0.9503041) / 1.08883;
    // XYZ to LAB
    x = x > CIE_EPSILON ? Math.pow(x, 1 / 3) : (CIE_KAPPA * x + 16) / 116;
    y = y > CIE_EPSILON ? Math.pow(y, 1 / 3) : (CIE_KAPPA * y + 16) / 116;
    z = z > CIE_EPSILON ? Math.pow(z, 1 / 3) : (CIE_KAPPA * z + 16) / 116;
    return {
        l: 116 * y - 16,
        a: 500 * (x - y),
        b: 200 * (y - z),
    };
}
export function labToRgb(lab) {
    let y = (lab.l + 16) / 116;
    let x = lab.a / 500 + y;
    let z = y - lab.b / 200;
    const y3 = y * y * y;
    const x3 = x * x * x;
    const z3 = z * z * z;
    y = y3 > CIE_EPSILON ? y3 : (116 * y - 16) / CIE_KAPPA;
    x = x3 > CIE_EPSILON ? x3 : (116 * x - 16) / CIE_KAPPA;
    z = z3 > CIE_EPSILON ? z3 : (116 * z - 16) / CIE_KAPPA;
    x *= 0.95047;
    z *= 1.08883;
    let r = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
    let g = x * -0.969266 + y * 1.8760108 + z * 0.041556;
    let b = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;
    // Linear to sRGB
    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
    b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;
    return {
        r: Math.round(clamp(r * 255, 0, 255)),
        g: Math.round(clamp(g * 255, 0, 255)),
        b: Math.round(clamp(b * 255, 0, 255)),
    };
}
// ============================================================================
// OKLAB (Björn Ottosson, 2020)
// ============================================================================
export function rgbToOklab(rgb) {
    let r = rgb.r / 255;
    let g = rgb.g / 255;
    let b = rgb.b / 255;
    // sRGB to linear
    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
    // Linear RGB to LMS
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);
    return {
        L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
        a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
        b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
    };
}
export function oklabToRgb(oklab) {
    const l_ = oklab.L + 0.3963377774 * oklab.a + 0.2158037573 * oklab.b;
    const m_ = oklab.L - 0.1055613458 * oklab.a - 0.0638541728 * oklab.b;
    const s_ = oklab.L - 0.0894841775 * oklab.a - 1.291485548 * oklab.b;
    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;
    let r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    let b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
    // Linear to sRGB
    r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
    b = b > 0.0031308 ? 1.055 * Math.pow(b, 1 / 2.4) - 0.055 : 12.92 * b;
    return {
        r: Math.round(clamp(r * 255, 0, 255)),
        g: Math.round(clamp(g * 255, 0, 255)),
        b: Math.round(clamp(b * 255, 0, 255)),
    };
}
// ============================================================================
// RYB (Red-Yellow-Blue, approximate)
// ============================================================================
/**
 * RGB → RYB, the exact inverse of {@link rybToRgb}.
 *
 * BUG-006 (2026-09-02 deep-dive): the previous implementation credited the
 * leftover green to BLUE only (`b__ = (b_ + (g_ - y)) / 2`) and never added any
 * of it back to yellow. Green therefore collapsed onto the blue axis: pure
 * green and pure blue both mapped to `{r:0, y:0, b:1}`, so every green and teal
 * that made a round trip came back blue — visibly, on the Discord bot's DEFAULT
 * `/mix` and `/gradient` mode.
 *
 * Reducing {@link rybToRgb} to its chromatic parts (white `w` removed) gives
 *
 *     R = r + y - min(y, b),   G = y,   B = b - min(y, b)
 *
 * which inverts to `y = G` plus two cases on whether any blue survives:
 *
 * - `B > 0` — blue dominates the yellow it was mixed with, so `r = R` and the
 *   green we owe comes back out of blue: `b = B + G`.
 * - `B == 0` — all the blue was consumed making green, so red and blue trade
 *   against `R - G`: a surplus is red, a deficit is blue.
 *
 * The final rescale mirrors the forward direction's, which normalises the
 * result back to the input's maximum chroma.
 */
export function rgbToRyb(rgb) {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const w = Math.min(r, g, b);
    const r_ = r - w;
    const g_ = g - w;
    const b_ = b - w;
    const mg = Math.max(r_, g_, b_);
    const y = g_;
    let r__;
    let b__;
    if (b_ > 0) {
        r__ = r_;
        b__ = b_ + g_;
    }
    else {
        const surplus = r_ - g_;
        r__ = surplus >= 0 ? surplus : 0;
        b__ = surplus >= 0 ? 0 : -surplus;
    }
    const n = Math.max(r__, y, b__) / Math.max(mg, 0.001);
    return {
        r: r__ / Math.max(n, 0.001) + w,
        y: y / Math.max(n, 0.001) + w,
        b: b__ / Math.max(n, 0.001) + w,
    };
}
export function rybToRgb(ryb) {
    const r = ryb.r;
    const y = ryb.y;
    const b = ryb.b;
    const w = Math.min(r, y, b);
    const r_ = r - w;
    const y_ = y - w;
    const b_ = b - w;
    const my = Math.max(r_, y_, b_);
    let g = Math.min(y_, b_);
    const y__ = y_ - g;
    const b__ = b_ - g;
    const r__ = r_ + y__;
    g = g + y__;
    const n = Math.max(r__, g, b__) / Math.max(my, 0.001);
    return {
        r: Math.round(clamp((r__ / Math.max(n, 0.001) + w) * 255, 0, 255)),
        g: Math.round(clamp((g / Math.max(n, 0.001) + w) * 255, 0, 255)),
        b: Math.round(clamp((b__ / Math.max(n, 0.001) + w) * 255, 0, 255)),
    };
}
// ============================================================================
// HSL
// ============================================================================
export function rgbToHsl(rgb) {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) {
        return { h: 0, s: 0, l };
    }
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    switch (max) {
        case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            break;
        case g:
            h = ((b - r) / d + 2) / 6;
            break;
        case b:
            h = ((r - g) / d + 4) / 6;
            break;
    }
    return { h: h * 360, s, l };
}
export function hslToRgb(hsl) {
    const h = hsl.h / 360;
    const s = hsl.s;
    const l = hsl.l;
    if (s === 0) {
        const v = Math.round(l * 255);
        return { r: v, g: v, b: v };
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue2rgb = (p, q, t) => {
        if (t < 0)
            t += 1;
        if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    return {
        r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hue2rgb(p, q, h) * 255),
        b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
}
// Kubelka-Munk helpers used to live here — `rgbToReflectance`,
// `reflectanceToRgb`, `reflectanceToKS`, `ksToReflectance`. The K/S formulas
// themselves were correct, but they were applied to the three gamma-encoded
// sRGB channels independently, which is not Kubelka-Munk: K-M is defined
// per-wavelength on a LINEAR spectral reflectance curve. Because K/S diverges
// as R -> 0, any channel dark in either input collapsed to ~0 at every ratio.
// `blendSpectral` now delegates to spectral.js, which reconstructs a real
// 38-band reflectance curve. See docs/research/2026-09-03-algorithm-fact-check.
// ============================================================================
// Utility
// ============================================================================
/**
 * Parse a hex color string into RGB channels.
 *
 * REFACTOR-005 (2026-07-18 audit): local implementation replacing the sole use
 * of `@xivdyetools/core`'s ColorService — the entire core package (dye
 * database, k-d tree, i18n) was a dependency for these ~10 lines. Matches
 * core's strict behavior: throws on malformed input; accepts #RGB and #RRGGBB
 * (leading # optional).
 */
export function hexToRgb(hex) {
    const clean = hex.startsWith('#') ? hex.slice(1) : hex;
    if (!/^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(clean)) {
        throw new Error(`Invalid hex color: ${hex}. Use format #RRGGBB or #RGB`);
    }
    const full = clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean;
    return {
        r: parseInt(full.substring(0, 2), 16),
        g: parseInt(full.substring(2, 4), 16),
        b: parseInt(full.substring(4, 6), 16),
    };
}
export function rgbToHex(rgb) {
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}
//# sourceMappingURL=conversions.js.map